import { ExIconButton, IconButtonType } from '@boomi/exosphere';
import classNames from 'classnames';
import { type ReactElement, useState } from 'react';
import { type AutomateError, markNotificationsAsRead } from '../api';
import { useApp } from '../common/AppProvider';
import Indicator from '../common/Indicator';
import TextWithArrows from '../common/TextWithArrows';
import { translations } from '../translations/translations';
import type { AutomationTask, Notification } from '../types/types';
import { getLocale } from '../utils/utils';

interface Props {
    notification: Notification;
    task: AutomationTask | undefined;
}

const NotificationItem = ({ notification, task }: Props) => {
    const [notificationExpanded, setNotificationExpanded] = useState(false);

    const { language, notifications, setNotifications, alertError, setIsLoading } = useApp();

    const readSingleMessage = async (notification: Notification) => {
        setIsLoading(true);

        await markNotificationsAsRead([notification.id])
            .then(() => {
                notifications.forEach((element, index) => {
                    if (element.id === notification.id) {
                        notifications[index].read = true;
                    }
                });
                setNotifications([...notifications]);
            })
            .catch((e: AutomateError) =>
                alertError({
                    message: translations[language].ERROR_reading_single_notification,
                    traceId: e.json?.traceId,
                }),
            )
            .finally(() => setIsLoading(false));
    };

    const splitNotificationText = (notificationText: string): string[] => {
        return notificationText.split(/:(.*)/s);
    };

    const shapeNotificationDetail = (key: string, value: string) => {
        let valueToDisplay: ReactElement = <>{value}</>;

        // This is a bit crude but it should handle anything that starts with http which will cover most links.
        // This could be done with regex but it doesn't need to be that complicated. We can improve it if there's too many false positives or negatives.
        if (value.length >= 4 && value.substring(0, 4).toLowerCase() === 'http') {
            valueToDisplay = (
                <a href={value} target="_blank" rel="noreferrer">
                    {valueToDisplay}
                </a>
            );
        }

        return (
            <div className="notification-additional-content" key={`${notification.id}-${key}`}>
                <strong>{key}:</strong> {valueToDisplay}
            </div>
        );
    };

    const tileClasses = classNames('user-notification-layout', 'ta-tile', 'ta-tile-interactive', {
        expanded: notificationExpanded,
    });

    const notificationTextArr = splitNotificationText(notification.message);
    const notificationDetails = notification?.additionalDetails
        ? Object.entries(notification.additionalDetails)
        : [];

    return (
        <li key={notification.id}>
            {/* Buttons don't allow users to select the text within them, so replacing
            the button with a clickable div here. */}
            <div
                className={tileClasses}
                tabIndex={0}
                // biome-ignore lint/a11y/useSemanticElements: Requires refactor
                role="button"
                onClick={() => {
                    if (!notification.read) {
                        readSingleMessage(notification);
                    }
                }}
                onKeyUp={(e) => {
                    if (!notification.read && (e.key === 'Enter' || e.key === 'Space')) {
                        readSingleMessage(notification);
                    }
                }}
            >
                <Indicator isOn={!notification.read} color="orange" />
                <div>
                    <span className="user-notification-message title md">
                        {!!task && <TextWithArrows text={`${task.displayName}: `} />}
                        <TextWithArrows text={notificationTextArr[0]} />
                    </span>
                    {notificationTextArr[1]?.length > 0 && (
                        <span className="user-notification-details text md">
                            {notificationTextArr[1]}
                        </span>
                    )}
                </div>

                <span className="user-notification-date text md">
                    {new Date(notification.createdAt).toLocaleString(getLocale())}
                </span>
                {notificationDetails.length > 0 && (
                    <ExIconButton
                        className="expandable-button"
                        icon={notificationExpanded ? 'Up caret' : 'Down caret'}
                        onClick={() => setNotificationExpanded(!notificationExpanded)}
                        type={IconButtonType.TERTIARY}
                        tooltipText={`${notificationExpanded ? 'Hide' : 'Show'} additional details.`}
                        data-testid={`${notification.id}-details-button`}
                    />
                )}
            </div>
            {notificationExpanded && (
                <div className="notification-additional-content-wrapper">
                    {notificationDetails.map((ad) => shapeNotificationDetail(ad[0], ad[1]))}
                </div>
            )}
        </li>
    );
};

export default NotificationItem;
